import { useMemo, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolInvoke, toolSuccess, toolError } from '@saas-pages/shared/public/runtime'
import { format, useFormatBytes, useT } from '@/shared/i18n'
import { PdfPagePreview } from '@/features/pdf-page-preview'
import { getUtilityDefinition } from '@/shared/utilities/utilities'
import {
  ROTATION_ANGLES,
  MAX_FILE_MB,
  validateFileSize,
  rotatedFilename,
  rotationLabel,
  parsePageRanges,
  type RotationAngle,
} from './lib'
import { FilePickPrompt } from '@/shared/ui/FilePickPrompt'
import { RevealOnMount } from '@/shared/ui/RevealOnMount'

const UTILITY = getUtilityDefinition('povernut').analyticsKey

// ─── State machine ────────────────────────────────────────────────────────────

type Phase =
  | { kind: 'idle' }
  | { kind: 'ready'; file: File }
  | { kind: 'loading'; file: File }
  | { kind: 'success'; file: File; blob: Blob; objectUrl: string; rotatedCount: number }
  | { kind: 'error'; file: File | null; message: string; errorCode: string }

type PagesMode = 'all' | 'ranges'

// ─── Drop-zone ────────────────────────────────────────────────────────────────

interface DropZoneProps {
  onFile: (file: File) => void
  disabled: boolean
}

function DropZone({ onFile, disabled }: DropZoneProps) {
  const t = useT()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    const file = files[0]
    if (!file) return
    if (!file.name.toLowerCase().endsWith('.pdf')) return
    onFile(file)
  }

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={t.common.pickPdf}
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          inputRef.current?.click()
        }
      }}
      onDragOver={(e) => {
        e.preventDefault()
        if (!disabled) setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDragging(false)
        if (!disabled) handleFiles(e.dataTransfer.files)
      }}
      className={cn(
        'flex flex-col items-center justify-center gap-3',
        'rounded-xl border-2 border-dashed p-6 sm:p-10 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'cursor-pointer select-none transition-colors',
        dragging && !disabled
          ? 'border-primary bg-primary/5'
          : 'border-border hover:border-primary/50 hover:bg-accent/30',
        disabled && 'pointer-events-none opacity-50',
      )}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-primary/60"
        aria-hidden="true"
      >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
      </svg>

      <div>
        <p className="text-sm font-medium text-foreground">
          <FilePickPrompt drag={t.common.dragPdf} />
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {format(t.common.pdfLimit, { size: MAX_FILE_MB })}
        </p>
        <p className="mt-1 flex items-center justify-center gap-1 text-[11px] text-muted-foreground/80">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          {t.common.localBadge}
        </p>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".pdf,application/pdf"
        className="sr-only"
        tabIndex={-1}
        onChange={(e) => handleFiles(e.target.files)}
        disabled={disabled}
      />
    </div>
  )
}

// ─── File badge ───────────────────────────────────────────────────────────────

interface FileBadgeProps {
  file: File
  onClear: () => void
  disabled: boolean
}

function FileBadge({ file, onClear, disabled }: FileBadgeProps) {
  const t = useT()
  const formatBytes = useFormatBytes()
  return (
    <div className="flex items-center gap-3 rounded-lg border bg-accent/30 px-4 py-3">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="shrink-0 text-primary"
        aria-hidden="true"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
      </svg>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{file.name}</p>
        <p className="text-xs text-muted-foreground">{formatBytes(file.size)}</p>
      </div>

      <button
        type="button"
        onClick={onClear}
        disabled={disabled}
        aria-label={t.common.deleteFile}
        className={cn(
          'shrink-0 rounded p-1 text-muted-foreground transition-colors',
          'hover:bg-destructive/10 hover:text-destructive',
          disabled && 'pointer-events-none opacity-50',
        )}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  )
}

// ─── Angle selector ───────────────────────────────────────────────────────────

interface AngleSelectorProps {
  value: RotationAngle
  onChange: (v: RotationAngle) => void
  disabled: boolean
}

function AngleSelector({ value, onChange, disabled }: AngleSelectorProps) {
  const t = useT()
  return (
    <fieldset disabled={disabled} className="space-y-2">
      <legend className="mb-2 text-sm font-medium text-foreground">{t.povernut.angleLegend}</legend>
      <div className="grid grid-cols-3 gap-2">
        {ROTATION_ANGLES.map((angle) => {
          const selected = value === angle
          return (
            <label
              key={angle}
              className={cn(
                'flex cursor-pointer items-center justify-center rounded-lg border p-3 text-sm font-medium transition-colors',
                selected
                  ? 'border-primary bg-primary/5 ring-1 ring-primary text-foreground'
                  : 'border-border text-foreground hover:border-primary/40 hover:bg-accent/20',
                disabled && 'pointer-events-none opacity-50',
              )}
            >
              <input
                type="radio"
                name="rotation-angle"
                value={angle}
                checked={selected}
                onChange={() => onChange(angle)}
                className="sr-only"
              />
              {rotationLabel(angle)}
            </label>
          )
        })}
      </div>
    </fieldset>
  )
}

// ─── Pages selector ───────────────────────────────────────────────────────────

interface PagesSelectorProps {
  mode: PagesMode
  onModeChange: (m: PagesMode) => void
  ranges: string
  onRangesChange: (s: string) => void
  disabled: boolean
}

function PagesSelector({
  mode,
  onModeChange,
  ranges,
  onRangesChange,
  disabled,
}: PagesSelectorProps) {
  const t = useT()
  return (
    <fieldset disabled={disabled} className="space-y-2">
      <legend className="mb-2 text-sm font-medium text-foreground">{t.povernut.pagesLegend}</legend>
      <div className="grid grid-cols-2 gap-2">
        {(['all', 'ranges'] as const).map((m) => {
          const selected = mode === m
          return (
            <label
              key={m}
              className={cn(
                'flex cursor-pointer items-center justify-center rounded-lg border p-3 text-sm font-medium transition-colors',
                selected
                  ? 'border-primary bg-primary/5 ring-1 ring-primary text-foreground'
                  : 'border-border text-foreground hover:border-primary/40 hover:bg-accent/20',
                disabled && 'pointer-events-none opacity-50',
              )}
            >
              <input
                type="radio"
                name="pages-mode"
                value={m}
                checked={selected}
                onChange={() => onModeChange(m)}
                className="sr-only"
              />
              {m === 'all' ? t.povernut.pagesAll : t.povernut.pagesRanges}
            </label>
          )
        })}
      </div>

      {mode === 'ranges' && (
        <div className="space-y-1.5 pt-1">
          <input
            type="text"
            value={ranges}
            onChange={(e) => onRangesChange(e.target.value)}
            placeholder={t.povernut.rangesPlaceholder}
            disabled={disabled}
            className={cn(
              'block w-full rounded-md border border-input bg-background px-3 py-2 text-sm',
              'placeholder:text-muted-foreground/70',
              'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
              disabled && 'pointer-events-none opacity-50',
            )}
            aria-label={t.povernut.rangesAria}
          />
          <p className="text-xs text-muted-foreground">
            {t.povernut.rangesHintPrefix}<code>1,3,5-7</code>{t.povernut.rangesHintSuffix}
          </p>
        </div>
      )}
    </fieldset>
  )
}

// ─── Result panel ─────────────────────────────────────────────────────────────

interface ResultPanelProps {
  file: File
  blob: Blob
  objectUrl: string
  rotatedCount: number
  onReset: () => void
}

function ResultPanel({ file, blob, objectUrl, rotatedCount, onReset }: ResultPanelProps) {
  const t = useT()
  const formatBytes = useFormatBytes()

  function handleDownload() {
    const a = document.createElement('a')
    a.href = objectUrl
    a.download = rotatedFilename(file.name)
    a.click()
  }

  return (
    <Card className="border-green-200 bg-green-50/50 dark:border-green-500/30 dark:bg-green-500/10">
      <CardContent className="p-5">
        <RevealOnMount />
        <div className="mb-4 flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0 text-green-600 dark:text-green-300"
            aria-hidden="true"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <span className="text-base font-semibold text-green-700 dark:text-green-200">{t.common.done}</span>
        </div>

        <div className="mb-4 space-y-1.5 text-sm">
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.common.file}</span>
            <span className="truncate text-right font-medium text-foreground">{file.name}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.povernut.rotatedCount}</span>
            <span className="font-medium text-foreground">{rotatedCount}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.common.size}</span>
            <span className="font-medium text-foreground">{formatBytes(blob.size)}</span>
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Button onClick={handleDownload} size="lg" className="flex-1">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            {t.common.download}
          </Button>
          <Button variant="outline" onClick={onReset} className="flex-1 sm:flex-none">
            {t.povernut.again}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

// ─── Error panel ──────────────────────────────────────────────────────────────

interface ErrorPanelProps {
  message: string
  onRetry: () => void
}

function ErrorPanel({ message, onRetry }: ErrorPanelProps) {
  const t = useT()
  return (
    <Card className="border-destructive/30 bg-destructive/5">
      <CardContent className="p-5">
        <div className="mb-3 flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0 text-destructive"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span className="text-sm font-semibold text-destructive">{t.common.errorTitle}</span>
        </div>
        <p className="mb-4 text-sm text-foreground">{message}</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t.common.retry}
        </Button>
      </CardContent>
    </Card>
  )
}

// ─── Spinner ──────────────────────────────────────────────────────────────────

function Spinner() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="animate-spin"
      aria-hidden="true"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  )
}

// ─── Main form ────────────────────────────────────────────────────────────────

export function PovernutForm() {
  const t = useT()
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' })
  const [angle, setAngle] = useState<RotationAngle>(90)
  const [pagesMode, setPagesMode] = useState<PagesMode>('all')
  const [rangesInput, setRangesInput] = useState('')
  const [previewTotalPages, setPreviewTotalPages] = useState<number | null>(null)

  const isLoading = phase.kind === 'loading'
  const hasFile = phase.kind === 'ready' || phase.kind === 'loading' || phase.kind === 'error'
  const currentFile = hasFile ? phase.file : null
  const selectedPreviewPages = useMemo(() => {
    if (previewTotalPages === null) return undefined
    if (pagesMode === 'all') {
      return new Set(Array.from({ length: previewTotalPages }, (_, index) => index + 1))
    }

    const parsed = parsePageRanges(rangesInput, previewTotalPages)
    if (parsed === null) return undefined
    return new Set(parsed.map((index) => index + 1))
  }, [pagesMode, previewTotalPages, rangesInput])
  const previewTransforms = useMemo(() => {
    if (!selectedPreviewPages) return undefined
    return new Map(
      Array.from(selectedPreviewPages, (pageNumber) => [pageNumber, { rotate: angle }]),
    )
  }, [angle, selectedPreviewPages])

  function handleFile(file: File) {
    if (!validateFileSize(file)) {
      toolError(UTILITY, 'client_too_large', { angle, pages_mode: pagesMode })
      setPhase({
        kind: 'error',
        file,
        errorCode: 'client_too_large',
        message: format(t.common.tooLarge, { size: MAX_FILE_MB }),
      })
      return
    }

    if (phase.kind === 'success') {
      URL.revokeObjectURL(phase.objectUrl)
    }

    setPhase({ kind: 'ready', file })
    setPreviewTotalPages(null)
  }

  function handleClear() {
    if (phase.kind === 'success') {
      URL.revokeObjectURL(phase.objectUrl)
    }
    setPhase({ kind: 'idle' })
    setPreviewTotalPages(null)
  }

  function handleRetry() {
    if (phase.kind === 'error' && phase.file) {
      setPhase({ kind: 'ready', file: phase.file })
    } else {
      setPhase({ kind: 'idle' })
    }
  }

  async function handleSubmit() {
    if (phase.kind !== 'ready') return
    const file = phase.file

    setPhase({ kind: 'loading', file })
    toolInvoke(UTILITY, { angle, pages_mode: pagesMode })

    const startMs = Date.now()

    try {
      const { PDFDocument, degrees } = await import('pdf-lib')
      const bytes = await file.arrayBuffer()
      const pdfDoc = await PDFDocument.load(bytes)
      const pages = pdfDoc.getPages()
      const totalPages = pages.length

      let indices: number[]
      if (pagesMode === 'all') {
        indices = pages.map((_, i) => i)
      } else {
        const parsed = parsePageRanges(rangesInput, totalPages)
        if (parsed === null) {
          toolError(UTILITY, 'invalid_ranges', { angle, pages_mode: pagesMode })
          setPhase({
            kind: 'error',
            file,
            errorCode: 'invalid_ranges',
            message: t.povernut.invalidRanges,
          })
          return
        }
        indices = parsed
      }

      for (const i of indices) {
        const page = pages[i]
        if (!page) continue
        const current = page.getRotation().angle
        page.setRotation(degrees((current + angle) % 360))
      }

      const out = await pdfDoc.save()
      const blob = new Blob([out], { type: 'application/pdf' })
      const durationMs = Date.now() - startMs
      const objectUrl = URL.createObjectURL(blob)

      toolSuccess(UTILITY, durationMs, {
        angle,
        pages_mode: pagesMode,
        total_pages: totalPages,
        rotated_pages: indices.length,
        output_bytes: blob.size,
      })

      setPhase({
        kind: 'success',
        file,
        blob,
        objectUrl,
        rotatedCount: indices.length,
      })
    } catch (err: unknown) {
      const code = err instanceof Error && err.message.includes('encrypted')
        ? 'encrypted'
        : 'parse_error'
      const message = code === 'encrypted' ? t.povernut.encrypted : t.povernut.parseError
      toolError(UTILITY, code, { angle, pages_mode: pagesMode })
      setPhase({ kind: 'error', file, errorCode: code, message })
    }
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  if (phase.kind === 'success') {
    return (
      <div className="space-y-4">
        <ResultPanel
          file={phase.file}
          blob={phase.blob}
          objectUrl={phase.objectUrl}
          rotatedCount={phase.rotatedCount}
          onReset={handleClear}
        />
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {currentFile ? (
        <FileBadge file={currentFile} onClear={handleClear} disabled={isLoading} />
      ) : (
        <DropZone onFile={handleFile} disabled={isLoading} />
      )}

      <AngleSelector value={angle} onChange={setAngle} disabled={isLoading} />

      <PagesSelector
        mode={pagesMode}
        onModeChange={setPagesMode}
        ranges={rangesInput}
        onRangesChange={setRangesInput}
        disabled={isLoading}
      />

      {currentFile && (
        <PdfPagePreview
          file={currentFile}
          totalPages={previewTotalPages}
          selectedPages={selectedPreviewPages}
          markerLabel={t.pdfPreview.rotated}
          transformByPage={previewTransforms}
          onDocumentLoad={setPreviewTotalPages}
        />
      )}

      {phase.kind === 'error' && <ErrorPanel message={phase.message} onRetry={handleRetry} />}

      <Button
        size="lg"
        className="sticky bottom-3 z-10 w-full shadow-lg"
        disabled={phase.kind !== 'ready' || isLoading}
        onClick={handleSubmit}
      >
        {isLoading ? (
          <>
            <Spinner />
            {t.povernut.loading}
          </>
        ) : (
          t.povernut.submit
        )}
      </Button>
    </div>
  )
}
